import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Container,
  VStack,
  Box,
  Heading,
  Text,
  NativeSelect,
  SimpleGrid,
  HStack,
  Badge,
  Spinner,
  Alert,
  Button,
  Dialog,
  Portal,
  Field,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import {
  FiBookOpen,
  FiClipboard,
  FiTag,
  FiMap,
  FiCalendar,
  FiRefreshCw,
} from 'react-icons/fi'
import { useContactLists, useMaterializeList } from '../hooks/useContactList'
import { useJobPolling } from '../hooks/useJobPolling'
import { canManuallyRefresh, isAutoDaily } from '../lib/listRefresh'
import { CanvassingToolCard } from '../components/canvassing/CanvassingToolCard'

export function CanvassingDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const listId = searchParams.get('listId')
  const [selectedListId, setSelectedListId] = useState<string>(listId || '')
  const [showRefreshDialog, setShowRefreshDialog] = useState(false)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const queryClient = useQueryClient()

  const { data: lists, isLoading, error } = useContactLists()
  const materializeMutation = useMaterializeList()

  // Materialize enqueues a background job (WI-42) rather than running inline (slice 6b). Mirrors
  // ListsPage's pattern: the dialog stays open (isRefreshing-gated) through the enqueue+poll
  // window -- Chakra unmounts AlertDialogContent the instant isOpen flips false, so closing right
  // after enqueue would make any poll-window loading state unobservable -- and the completion
  // effect below is what actually fires the success/error toast and closes the dialog.
  const [refreshJobId, setRefreshJobId] = useState<string | null>(null)
  const [refreshListId, setRefreshListId] = useState<string | null>(null)
  const { data: refreshJob, isTerminal: refreshTerminal } = useJobPolling(refreshJobId)
  const isRefreshing = materializeMutation.isPending || (!!refreshJobId && !refreshTerminal)

  // Sync URL param with local state
  useEffect(() => {
    if (listId && listId !== selectedListId) {
      setSelectedListId(listId)
    }
  }, [listId, selectedListId])

  const handleListChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newListId = e.target.value
    setSelectedListId(newListId)
    if (newListId) {
      setSearchParams({ listId: newListId })
    } else {
      setSearchParams({})
    }
  }

  const selectedList = lists?.find(l => l.id === selectedListId)

  const handleRefreshClick = () => {
    setShowRefreshDialog(true)
  }

  const handleConfirmRefresh = async () => {
    if (!selectedListId) return

    try {
      const { jobId } = await materializeMutation.mutateAsync(selectedListId)
      // Enqueued (202) -- not refreshed yet. Keep the dialog open (isRefreshing) through the
      // poll; the completion effect below closes it and fires the success/error toast once the
      // job actually reaches a terminal state. refreshListId is captured separately from
      // selectedListId (rather than read off it in the effect) because the dropdown stays live
      // while the dialog is open -- if the user switched lists mid-poll, reading selectedListId
      // in the effect would invalidate caches for the wrong list.
      setRefreshListId(selectedListId)
      setRefreshJobId(jobId)
    } catch (err) {
      // Failed to even enqueue (e.g. network/validation error on the POST itself).
      toaster.create({
        title: 'Refresh failed',
        description: 'An error occurred while refreshing the list.',
        type: 'error',
        duration: 5000,
      })
      setShowRefreshDialog(false)
    }
  }

  // Drives the materialize job to completion (WI-42). Branches on `!refreshTerminal` alone --
  // NOT `!refreshTerminal || !refreshJob` -- because a 404 (job not owned / not found) reports
  // isTerminal=true with job undefined; guarding on `!refreshJob` too would swallow that case and
  // leave the dialog spinning forever. Invalidation happens here, on completion, never on the
  // enqueue mutation's onSuccess -- materialize genuinely persists (rewrites list membership,
  // stamps IsMaterialized/memberCount), so invalidating before the job finishes would just
  // refetch stale pre-job data.
  useEffect(() => {
    if (!refreshTerminal) return

    if (refreshJob?.status === 'Succeeded') {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
      if (refreshListId) {
        queryClient.invalidateQueries({ queryKey: ['lists', refreshListId] })
        queryClient.invalidateQueries({ queryKey: ['lists', refreshListId, 'overview'] })
      }
      toaster.create({
        title: 'List refreshed',
        description: 'The list has been refreshed with the latest data.',
        type: 'success',
        duration: 4000,
      })
    } else {
      toaster.create({
        title: 'Refresh failed',
        description: refreshJob?.errorMessage ?? 'An error occurred while refreshing the list.',
        type: 'error',
        duration: 5000,
      })
    }
    setRefreshJobId(null)
    setRefreshListId(null)
    setShowRefreshDialog(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTerminal, refreshJob])

  const handleCloseRefreshDialog = () => {
    if (isRefreshing) return
    setShowRefreshDialog(false)
  }

  const getStrategyColor = (strategy: string) => {
    switch (strategy) {
      case 'Dynamic':
        return 'green'
      case 'AutoDaily':
        return 'blue'
      case 'Manual':
        return 'orange'
      case 'Locked':
        return 'gray'
      default:
        return 'gray'
    }
  }

  const formatLastRefreshed = (date: string | null) => {
    if (!date) return 'Never'
    const refreshed = new Date(date)
    const now = new Date()
    const diffMs = now.getTime() - refreshed.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 60) return `${diffMins} minutes ago`
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`
    if (diffDays === 1) return '1 day ago'
    return `${diffDays} days ago`
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack gap={6} align="stretch">
        <Box>
          <Heading size="lg">Canvassing</Heading>
          <Text color="gray.500" mt={1}>
            Plan and manage canvassing operations
          </Text>
        </Box>

        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <Field.Root>
            <Field.Label fontWeight="semibold">Contact List</Field.Label>
            {isLoading && (
              <HStack gap={2}>
                <Spinner size="sm" color="accent" />
                <Text fontSize="sm" color="gray.500">
                  Loading lists...
                </Text>
              </HStack>
            )}

            {!!error && (
              <Alert.Root status="error" borderRadius="md" size="sm">
                <Alert.Indicator />
                Failed to load contact lists
              </Alert.Root>
            )}

            {!isLoading && lists && (
              <>
                <NativeSelect.Root disabled={isRefreshing} size="md">
                  <NativeSelect.Field
                    placeholder="Select a Contact List..."
                    value={selectedListId}
                    onChange={handleListChange}>
                    {lists.map(list => (
                      <option key={list.id} value={list.id}>
                        {list.label} ({list.memberCount?.toLocaleString() ?? 0} contacts)
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>

                {selectedList && (
                  <Box
                    mt={4}
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    borderLeft="4px solid"
                    borderColor="info.fg"
                  >
                    <HStack justify="space-between" mb={2}>
                      <HStack gap={3} flexWrap="wrap">
                        <Text fontWeight="bold" color="gray.800">
                          {selectedList.label}
                        </Text>
                        <Text color="gray.600" fontSize="sm">
                          |
                        </Text>
                        <Text color="gray.700" fontSize="sm">
                          {selectedList.memberCount?.toLocaleString() ?? 0} contacts
                        </Text>
                        <Text color="gray.600" fontSize="sm">
                          |
                        </Text>
                        <Badge
                          colorPalette={getStrategyColor(selectedList.refreshStrategy)}
                          fontSize="xs"
                        >
                          {selectedList.refreshStrategy}
                        </Badge>
                      </HStack>
                      {canManuallyRefresh(selectedList) && (
                        <Button
                          size="sm"
                          colorPalette="green"
                          variant="outline"
                          onClick={handleRefreshClick}
                          loading={isRefreshing}><FiRefreshCw />Refresh
                                                  </Button>
                      )}
                    </HStack>
                    {isAutoDaily(selectedList) && (
                      <Text fontSize="xs" color="ink.2">Refreshes automatically each day</Text>
                    )}
                    <Text color="gray.600" fontSize="xs">
                      Last refreshed: {formatLastRefreshed(selectedList.lastRefreshed ?? null)}
                    </Text>
                  </Box>
                )}
              </>
            )}
          </Field.Root>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
          <CanvassingToolCard
            icon={FiBookOpen}
            title="Scripts"
            description="Write and manage scripts that canvassers follow during sessions."
            to="/dashboard/canvassing/scripts"
            listId={selectedListId || undefined}
          />
          <CanvassingToolCard
            icon={FiClipboard}
            title="Surveys"
            description="Create survey questions and answer sets to collect data during canvas."
            to="/dashboard/canvassing/surveys"
            listId={selectedListId || undefined}
          />
          <CanvassingToolCard
            icon={FiTag}
            title="Tags"
            description="Define tags that canvassers can apply to contacts."
            to="/dashboard/canvassing/tags"
            listId={selectedListId || undefined}
          />
          <CanvassingToolCard
            icon={FiMap}
            title="Turf Cutter"
            description="Draw geographic boundaries on a map to create turfs."
            to="/dashboard/canvassing/turf-cutter"
            listId={selectedListId || undefined}
          />
          <CanvassingToolCard
            icon={FiCalendar}
            title="Schedule Itinerary"
            description="Plan and schedule canvassing sessions and routes."
            to="/dashboard/canvassing/schedule"
            listId={selectedListId || undefined}
            isDisabled
          />
        </SimpleGrid>
      </VStack>

      {/* Refresh Confirmation Dialog */}
      <Dialog.Root
        open={showRefreshDialog}
        initialFocusEl={() => cancelRef.current}
        closeOnInteractOutside={!isRefreshing}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            handleCloseRefreshDialog();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Refresh List
                </Dialog.Header>

                <Dialog.Body>
                  <Text mb={3}>
                    Are you sure you want to refresh <strong>"{selectedList?.label}"</strong>?
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    This will update all voter file-sourced data for contacts in this list, but will
                    preserve all internal data from canvassing sessions (tags, survey responses, notes, etc.).
                  </Text>
                </Dialog.Body>

                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={handleCloseRefreshDialog} disabled={isRefreshing}>
                    Cancel
                  </Button>
                  <Button
                    colorPalette="green"
                    onClick={handleConfirmRefresh}
                    ml={3}
                    loading={isRefreshing}
                  >
                    Refresh
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </Container>
  );
}
